import React, { useEffect, useState } from 'react';
import { crmService } from '../../services/crm.service';
import { crmErrorMessage } from '../../utils/crm';
import { localDateString } from '../../utils/crmPipeline';

const KPIS = [
  { key: 'new',  label: 'New leads' },
  { key: 'work', label: 'Being worked' },
  { key: 'won',  label: 'Won',  tone: 'green' },
  { key: 'conv', label: 'Conversion rate' },
];

const rangeDates = (range) => {
  const today = new Date();
  const to = localDateString(today);
  if (range === 'week') {
    const dayOfWeek = (today.getDay() + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - dayOfWeek);
    return { from: localDateString(monday), to };
  }
  if (range === 'month') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: localDateString(first), to };
  }
  return { from: '', to: '' };
};

/**
 * Four summary tiles on Today. Values are computed from one pipeline call per side (both sides
 * ship all status counts whatever the view) and one listPersons count for new-leads-in-range.
 * Tapping a tile asks the parent to navigate — see CrmPage onOpenKpi.
 */
const TodayKpis = ({ book, ownerUserId, range, customFrom, customTo, reloadKey, onOpenKpi }) => {
  const [state, setState] = useState({ status: 'loading', values: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    const { from, to } = range === 'custom' ? { from: customFrom, to: customTo } : rangeDates(range);
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    (async () => {
      try {
        const [buy, sell, newLeads] = await Promise.all([
          crmService.getPipeline(book, 'BUY', { ownerUserId }),
          crmService.getPipeline(book, 'SELL', { ownerUserId }),
          from && to
            ? crmService.listPersons(book, { ownerUserId, addedFrom: from, addedTo: to, size: 1 })
            : Promise.resolve({ total: 0 }),
        ]);
        if (cancelled) return;
        const sum = (k) => (buy.counts?.[k] || 0) + (sell.counts?.[k] || 0);
        const won = sum('WON');
        const lost = sum('LOST');
        const conv = won + lost === 0 ? '—' : `${Math.round((won / (won + lost)) * 100)}%`;
        setState({
          status: 'ready',
          values: {
            new: newLeads.total || 0,
            work: sum('OPEN') + sum('NURTURING'),
            won,
            conv,
          },
          error: '',
        });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', values: null, error: crmErrorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [book, ownerUserId, range, customFrom, customTo, reloadKey]);

  if (state.status === 'error') return <p className="crm-error" role="alert">{state.error}</p>;
  const v = state.values || { new: '…', work: '…', won: '…', conv: '…' };

  return (
    <div className="crm-kpis">
      {KPIS.map((k) => (
        <button
          type="button"
          key={k.key}
          className={`crm-kpi ${k.tone ? `crm-kpi-${k.tone}` : ''}`}
          onClick={() => onOpenKpi(k.key)}
          aria-label={`${k.label}: ${v[k.key]}`}
        >
          <div className="crm-kpi-label">{k.label}</div>
          <div className="crm-kpi-value">{v[k.key]}</div>
        </button>
      ))}
    </div>
  );
};

export default TodayKpis;
